import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'

import { useTypedSelector } from 'src/State/RootReducer'
import { startInfinityUpdate, verifyRequirements } from '../api/infinity-update.api'
import { useUserByIdData } from '../../user/hooks/use-user-by-id'
import { SERVICE_LEVELS_ALL } from 'src/Configuration/user'
import { useFarmContext } from 'src/Elements/Farm'
import { usePortalQuery, usePortalMutation } from 'src/Libs/api'

interface InifinityUpdateResult {
  isInfinityUpdatePossible: boolean
  runInfinityUpdate: () => void
}

interface UseInfinityUpdateParams {
  systemId?: string
  onSuccess?: () => void
  onError?: () => void
}

type DataReturnType = [InifinityUpdateResult, boolean]

const useInfinityUpdate = ({ systemId, onError, onSuccess }: UseInfinityUpdateParams): DataReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const { isFarmMasterSelected } = useFarmContext()
  const userId = useTypedSelector(state => state.auth.userId)

  const verifyRequirementsQueryFn = async (): Promise<boolean | null> => {
    const { error, hasErrors, value } = await verifyRequirements(systemId, authFetch)

    if (hasErrors) {
      throw error || new Error('An unexpected error occurred when verifying requirements')
    }

    return value
  }

  const { data } = usePortalQuery({
    queryKey: ['hardware-tests', 'storages', systemId, 'infinity-update', 'verify-requirements'],
    queryFn: verifyRequirementsQueryFn,
    enabled: !!systemId,
  })

  const [userData] = useUserByIdData(userId)

  const { mutate: runInfinityUpdate, isPending: isLoading } = usePortalMutation({
    mutationFn: () => startInfinityUpdate(systemId, authFetch),
    onSuccess: () => onSuccess?.(),
    onError: () => onError?.(),
  })

  const isCurrentUserAllowed = !!(
    userData &&
    ((userData.level === 'INSTALLER' && userData.infinityCertified) || SERVICE_LEVELS_ALL.includes(userData.level))
  )
  const isNotFarm = !isFarmMasterSelected
  const isApprovedByServer = !!data

  const isInfinityUpdatePossible = isCurrentUserAllowed && isNotFarm && isApprovedByServer

  return [
    {
      isInfinityUpdatePossible,
      runInfinityUpdate,
    },
    isLoading,
  ]
}

export default useInfinityUpdate
